import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customInstance } from '../../services/api-client';
import {
  approvalGates,
  impactLines,
  journalEntries,
  legalMatters,
  sourceTabs,
  unusualItems,
  CLIENT_NAME,
  JOURNAL_DATE,
  PERIOD_LABEL,
  SOURCE_AGENT,
} from './data';
import type {
  AgentReview,
  AppletDataSourceConfig,
  ConnectorOption,
  GateStatus,
  JudgmentalAccrualsWorkbook,
  TabularData,
} from './types';

const BASE = '/api/judgmental-accruals';

const keys = {
  all: ['judgmental-accruals'] as const,
  workbook: () => [...keys.all, 'workbook'] as const,
  decisions: () => [...keys.all, 'decisions'] as const,
  config: () => [...keys.all, 'config'] as const,
  connectors: () => [...keys.all, 'connectors'] as const,
};

function toTabular<T extends Record<string, unknown>>(rows: T[], columns: string[]): TabularData {
  return {
    columns,
    rows: rows.map(row => columns.map(column => row[column])),
    row_count: rows.length,
  };
}

export const fallbackWorkbook: JudgmentalAccrualsWorkbook = {
  periodLabel: PERIOD_LABEL,
  journalDate: JOURNAL_DATE,
  clientName: CLIENT_NAME,
  sourceAgent: SOURCE_AGENT,
  sourceMode: 'fixture_fallback',
  redshiftSchema: 'judgmental_accruals',
  legalMatters,
  unusualItems,
  approvalGates,
  journalEntries,
  impactLines,
  sourceTabs,
  tables: {
    Legal_Contingency_Log: toTabular(
      legalMatters as unknown as Record<string, unknown>[],
      ['id', 'description', 'counterparty', 'assessment', 'lowEstimate', 'highEstimate', 'recommendedAccrual', 'treatment'],
    ),
    Unusual_Items_Log: toTabular(
      unusualItems as unknown as Record<string, unknown>[],
      ['id', 'description', 'amount', 'treatment', 'judgmentRequired', 'standard'],
    ),
    NetSuite_JE_Export: toTabular(
      journalEntries.flatMap(entry => entry.lines) as unknown as Record<string, unknown>[],
      ['externalId', 'journalDate', 'memo', 'accountNumber', 'accountName', 'debit', 'credit', 'department', 'className', 'subsidiary', 'approver'],
    ),
    Financial_Statement_Impact: toTabular(
      impactLines as unknown as Record<string, unknown>[],
      ['label', 'account', 'amount', 'driver', 'statement'],
    ),
  },
};

export const fallbackAgentReview: AgentReview = {
  summary: 'Run agent review after the Redshift workbook is loaded.',
  riskLevel: 'medium',
  sourceMode: 'unconfigured',
  redshiftSchema: 'judgmental_accruals',
  findings: [
    'Agent review is user-initiated and uses the currently loaded workbook.',
  ],
  requiredActions: ['Connect the applet to a Redshift schema before running review.'],
  usedLlm: false,
};

export function useJudgmentalAccrualsWorkbook() {
  return useQuery<JudgmentalAccrualsWorkbook>({
    queryKey: keys.workbook(),
    queryFn: () => customInstance<JudgmentalAccrualsWorkbook>({ url: `${BASE}/workbook` }),
    initialData: process.env.NODE_ENV === 'test' ? fallbackWorkbook : undefined,
    enabled: process.env.NODE_ENV !== 'test',
    staleTime: 60_000,
  });
}

export function useRefreshAgentReview() {
  return useMutation<AgentReview, Error, { statuses: Record<string, GateStatus>; focus?: string }>({
    mutationFn: ({ statuses, focus }) =>
      customInstance<AgentReview>({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { statuses, focus },
      }),
  });
}

export interface GateDecisionsPayload {
  statuses: Record<string, GateStatus>;
  auditLog: Record<string, unknown>[];
}

export function useGateDecisions() {
  return useQuery<GateDecisionsPayload>({
    queryKey: keys.decisions(),
    queryFn: () => customInstance<GateDecisionsPayload>({ url: `${BASE}/decisions` }),
    initialData: { statuses: {}, auditLog: [] },
    enabled: process.env.NODE_ENV !== 'test',
    staleTime: 30_000,
  });
}

export function useSaveGateDecisions() {
  const queryClient = useQueryClient();
  return useMutation<GateDecisionsPayload, Error, GateDecisionsPayload>({
    mutationFn: payload =>
      customInstance<GateDecisionsPayload>({
        url: `${BASE}/decisions`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: data => {
      queryClient.setQueryData(keys.decisions(), data);
    },
  });
}

export function useDataSourceConfig() {
  return useQuery<AppletDataSourceConfig>({
    queryKey: keys.config(),
    queryFn: () => customInstance<AppletDataSourceConfig>({ url: `${BASE}/config/data-source` }),
    staleTime: 60_000,
  });
}

export function useAvailableConnectors() {
  return useQuery<ConnectorOption[]>({
    queryKey: keys.connectors(),
    queryFn: () => customInstance<ConnectorOption[]>({ url: `${BASE}/config/available-connectors` }),
    staleTime: 60_000,
  });
}

export function useSetDataSource() {
  const queryClient = useQueryClient();
  return useMutation<AppletDataSourceConfig, Error, { connector_id: string; redshift_schema?: string }>({
    mutationFn: payload =>
      customInstance<AppletDataSourceConfig>({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: data => {
      queryClient.setQueryData(keys.config(), data);
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useClearDataSource() {
  const queryClient = useQueryClient();
  return useMutation<AppletDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance<AppletDataSourceConfig>({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: data => {
      queryClient.setQueryData(keys.config(), data);
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}
